import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UserFormService } from '@component/user/services/user-form.service';
import { UserService } from '@component/user/services/user.service';
import { UserEditDto } from '@model/user/user-edit-dto';
import { UserProfileDto } from '@model/user/user-profile-dto';
import { AuthMsalService } from '@shared/services/auth-msal.service';
import { NotificationService } from '@shared/services/notification.service';
import { RoleService } from '@shared/services/role.service';
import { UserRole } from '@shared/enums/user-role-enum';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { switchMap, take, map, filter } from 'rxjs';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzRadioModule } from "ng-zorro-antd/radio";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { normalizeLanguage } from '@shared/util/language.util';

@Component({
  selector: 'app-user-profile-edit',
  standalone: true,
  imports: [
    NzCardModule,
    NzFormModule,
    ReactiveFormsModule,
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzDividerModule,
    NzAutocompleteModule,
    NzRadioModule,
    TranslateModule
  ],
  templateUrl: './user-profile-edit.component.html',
  styleUrl: './user-profile-edit.component.scss'
})
export class UserProfileEditComponent implements OnInit {
  /**
   * Drawer reference
   *
   * @private
   * @type {NzDrawerRef}
   */
  private drawerRef: NzDrawerRef = inject(NzDrawerRef);

  /**
   * User connected profile
   *
   * @public
   * @type {!UserProfileDto}
   */
  public user!: UserProfileDto
  /**
 * User profile form
 *
 * @public
 * @type {!FormGroup}
 */
  public profileForm!: FormGroup;

  public optionsDomain: string[] = [];

  constructor(
    private readonly _userFormService: UserFormService,
    private readonly _notificationService: NotificationService,
    private readonly _userService: UserService,
    private readonly _authMsalService: AuthMsalService,
    private readonly _translateService: TranslateService,
    private readonly _roleService: RoleService
  ) { }


  ngOnInit(): void {
    this.loadUserProfile();
  }

  public get isAdmin(): boolean {
    return !!this.user.role && this._roleService.hasHigherRole(this.user.role.name, UserRole.ADMIN);
  }

  /**
 * Logout user
 *
 * @public
 */
  public logout(): void {
    this._authMsalService.logout()
  }

  /**
   * Submit user edit profile
   *
   * @public
   */
  public onSubmit() {
    const newUser: UserEditDto = this._userFormService.sanitizeProfileData(this.profileForm, this.user)
    this._userService.updateProfileRequest(newUser).pipe(
      // Get updated user
      switchMap((updatedUser) => {
        return this._authMsalService.getCurrentUser().pipe(
          take(1), // Get last value without staying subscribed
          switchMap(() => {
            // Reload connected user
            return this._authMsalService.fetchAuthenticatedUser().pipe(
              map(() => updatedUser)  // Return connected user updated
            );
          })
        )
      })
    ).subscribe({
      // Switch to the newly saved language ourselves instead of relying on AppComponent's async reaction to the user change,
      // otherwise the notification can still render with the previous language.
      next: (updatedUser) => {
        const newLang = normalizeLanguage(updatedUser.langageUser?.label);
        this._translateService.use(newLang).subscribe(() => {
          const successTitle = `<h2>${this._translateService.instant('profil.submitSuccessTitle')}</h2>`;
          const successText = `<h3>${this._translateService.instant('profil.submitSuccessText')}</h3>`;
          this._notificationService.successNotification(successTitle, successText, this._notificationService.getDefaultSuccessOptions());
          this.drawerRef.close();
        });
      },
      error: () => {
        const errorTitle = `<h2>${this._translateService.instant('profil.submitFailTitle')}</h2>`;
        const errorText = `<h3>${this._translateService.instant('profil.submitFailText')}</h3>`;
        this._notificationService.errorNotification(errorTitle, errorText, this._notificationService.getDefaultErrorOptions());
      }
    });
  }


  /**
   * Load connected user's profile
   *
   * @private
   */
  private loadUserProfile() {
    this._authMsalService.getCurrentUser().pipe(
      filter(user => !!user),
      take(1)
    ).subscribe({
      next: user => {
        this.user = user
        if (user.role) {
          this.initializeProfileForm(user);
        }
      }
    })
  }

  /**
* Initialize profile form
*
* @private
* @param {UserProfileDto} userConnected
*/
  private initializeProfileForm(userConnected: UserProfileDto): void {
    const userToEdit = userConnected as UserEditDto;
    this.profileForm = this._userFormService.initializeProfileForm(userToEdit);

    const emailControl = this.profileForm.get('preferredEmail');
    emailControl?.valueChanges.subscribe(value => {
      if (value) {
        const cleaned = value.replace(/\s+/g, ''); // Remove all whitespace
        if (value !== cleaned) {
          emailControl.setValue(cleaned, { emitEvent: false });
        }
      }
    });
  }

  /**
   * Handle input event for email domain suggestions
   *
   * @param e Event
   */
  onInput(e: Event): void {
    const value = (e.target as HTMLInputElement).value;
    if (!value || value.indexOf('@') >= 0) {
      this.optionsDomain = [];
    } else {
      this.optionsDomain = ['@elior.net', '@derichebourg-multiservices.com', '.external@airbus.com', '@airbus.com'].map(domain => `${value}${domain}`);
    }
  }
}
